import { useState, useEffect } from 'react';
import { useNavigate }         from 'react-router-dom';
import { useAuthStore }        from '../store/authStore';
import { useToast }            from '../components/Toast';

// ── XP Level helper (Feature 9 — client-side only) ─────────────────────────
function getLevel(xp) {
  if (xp < 100)   return { level: 1, name: 'Seedling',   emoji: '🌱' };
  if (xp < 300)   return { level: 2, name: 'Reader',     emoji: '📖' };
  if (xp < 600)   return { level: 3, name: 'Scholar',    emoji: '🖊️' };
  if (xp < 1000)  return { level: 4, name: 'Researcher', emoji: '🔬' };
  if (xp < 1800)  return { level: 5, name: 'Graduate',   emoji: '🎓' };
  if (xp < 3000)  return { level: 6, name: 'Professor',  emoji: '🏛️' };
  if (xp < 5000)  return { level: 7, name: 'Mastermind', emoji: '🧠' };
  return           { level: 8, name: 'Legend',      emoji: '👑' };
}

// ── Stats card ─────────────────────────────────────────────────────────────
function StatCard({ label, value, sub }) {
  return (
    <div className="glass rounded-xl p-4 flex flex-col gap-1">
      <p className="text-white/40 text-xs uppercase tracking-wider">{label}</p>
      <p className="text-2xl font-bold text-white">{value}</p>
      {sub && <p className="text-white/40 text-xs">{sub}</p>}
    </div>
  );
}

// ── Section heading ────────────────────────────────────────────────────────
function SectionHead({ children }) {
  return (
    <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
      {children}
    </h2>
  );
}

export default function Profile() {
  const navigate    = useNavigate();
  const toast       = useToast();
  const { user, updateProfile, signOut } = useAuthStore();

  const [displayName,    setDisplayName]    = useState(user?.displayName ?? '');
  const [avatarIndex,    setAvatarIndex]    = useState(user?.avatarIndex ?? 0);
  const [oldPass,        setOldPass]        = useState('');
  const [newPass,        setNewPass]        = useState('');
  const [confirmPass,    setConfirmPass]    = useState('');
  const [isSaving,       setIsSaving]       = useState(false);
  const [changingPass,   setChangingPass]   = useState(false);

  // Derive level from XP
  const xp    = user?.xp ?? 0;
  const level = getLevel(xp);



  const AVATARS = ['🦁', '🐺', '🦊', '🐻', '🐼', '🦅', '🦋', '🐲'];

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!displayName.trim()) { toast.error('Display name cannot be empty.'); return; }
    setIsSaving(true);
    try {
      await updateProfile({ displayName: displayName.trim(), avatarIndex });
      toast.success('Profile updated!');
    } catch {
      toast.error('Failed to update profile.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPass.length < 8) { toast.error('New password must be at least 8 characters.'); return; }
    if (newPass !== confirmPass) { toast.error('Passwords do not match.'); return; }
    setChangingPass(true);
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/users/me/password`, {
        method:  'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${useAuthStore.getState().token}` },
        body:    JSON.stringify({ oldPassword: oldPass, newPassword: newPass }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error);
      toast.success('Password changed!');
      setOldPass(''); setNewPass(''); setConfirmPass('');
    } catch (err) {
      toast.error(err.message || 'Failed to change password.');
    } finally {
      setChangingPass(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  if (!user) return null;

  const totalHours   = Math.floor((user.totalSeconds ?? 0) / 3600);
  const totalMinutes = Math.floor(((user.totalSeconds ?? 0) % 3600) / 60);

  return (
    <div className="min-h-screen p-4 md:p-8 max-w-2xl mx-auto space-y-6">

      {/* ── Header ── */}
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-2xl glass flex items-center justify-center text-4xl select-none">
          {AVATARS[avatarIndex]}
        </div>
        <div>
          <h1 className="heading text-2xl font-bold text-white">{user.displayName}</h1>
          <p className="text-white/40 text-sm">{user.email}</p>
          {/* Level badge */}
          <span className="inline-flex items-center gap-1 mt-1 text-xs font-semibold px-2 py-0.5 rounded-full"
            style={{ background: 'rgba(99,102,241,0.2)', border: '1px solid rgba(99,102,241,0.4)', color: '#818cf8' }}>
            {level.emoji} Lv{level.level} {level.name}
          </span>
        </div>
        {/* XP pill */}
        <div className="ml-auto text-right">
          <p className="text-xs text-white/40">XP</p>
          <p className="text-xl font-bold text-indigo-400">⚡ {xp}</p>
        </div>
      </div>

      {/* ── Stats ── */}
      <section>
        <SectionHead>📊 Stats</SectionHead>
        <div className="grid grid-cols-2 gap-3">
          <StatCard label="Total Hours"  value={`${totalHours}h ${totalMinutes}m`} />
          <StatCard label="Streak"       value={`🔥 ${user.streakDays ?? 0} days`} />
          <StatCard label="XP Earned"    value={`⚡ ${xp}`} sub={`Level ${level.level} — ${level.name}`} />
          <StatCard label="Group Role"   value={user.role ?? 'user'} />
        </div>
      </section>

      {/* ── Edit Profile ── */}
      <section>
        <SectionHead>✏️ Edit Profile</SectionHead>
        <form onSubmit={handleSaveProfile} className="glass rounded-xl p-5 space-y-4">
          {/* Avatar picker */}
          <div>
            <p className="text-white/50 text-xs mb-2">Choose avatar</p>
            <div className="flex flex-wrap gap-2">
              {AVATARS.map((av, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setAvatarIndex(i)}
                  className="w-10 h-10 rounded-xl text-xl transition-all duration-150 flex items-center justify-center"
                  style={{
                    background:  i === avatarIndex ? 'rgba(99,102,241,0.3)' : 'rgba(255,255,255,0.05)',
                    border:      i === avatarIndex ? '2px solid #6366f1' : '2px solid transparent',
                    transform:   i === avatarIndex ? 'scale(1.1)' : 'scale(1)',
                  }}
                >
                  {av}
                </button>
              ))}
            </div>
          </div>

          {/* Display name */}
          <div>
            <label className="text-white/50 text-xs block mb-1" htmlFor="profile-name">Display name</label>
            <input
              id="profile-name"
              className="input-glass w-full text-sm"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              maxLength={40}
            />
          </div>

          <button
            type="submit"
            disabled={isSaving}
            className="btn-primary w-full disabled:opacity-60"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      </section>

      <section>
        <SectionHead>🔒 Change Password</SectionHead>
        <form onSubmit={handleChangePassword} className="glass rounded-xl p-5 space-y-3">
          <input
            type="password"
            placeholder="Current password"
            value={oldPass}
            onChange={e => setOldPass(e.target.value)}
            className="input-glass w-full text-sm"
            id="profile-old-pass"
          />
          <input
            type="password"
            placeholder="New password (min 8 chars)"
            value={newPass}
            onChange={e => setNewPass(e.target.value)}
            className="input-glass w-full text-sm"
            id="profile-new-pass"
          />
          <input
            type="password"
            placeholder="Confirm new password"
            value={confirmPass}
            onChange={e => setConfirmPass(e.target.value)}
            className="input-glass w-full text-sm"
            id="profile-confirm-pass"
          />
          <button
            type="submit"
            disabled={changingPass}
            className="btn-primary w-full disabled:opacity-60"
          >
            {changingPass ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </section>

      {/* ── Danger Zone ── */}
      <section>
        <SectionHead>⚠️ Account</SectionHead>
        <div className="glass rounded-xl p-5">
          <button
            onClick={handleSignOut}
            className="w-full py-2 rounded-lg text-sm font-semibold text-red-400 transition-all duration-200"
            style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}
          >
            Sign out
          </button>
        </div>
      </section>
    </div>
  );
}
